import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { promoOfferApi } from '../api/promoOfferApi';
import type { CreatePromoOfferDTO, UpdatePromoOfferDTO, PromoOfferListResponse } from '../types';

export const promoOfferQueryKeys = {
  all: ['promo-offers'] as const,
  lists: () => [...promoOfferQueryKeys.all, 'list'] as const,
  list: (filters?: unknown) => [...promoOfferQueryKeys.lists(), filters] as const,
  details: () => [...promoOfferQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...promoOfferQueryKeys.details(), id] as const,
};

export const usePromoOffers = (query?: string) => {
  return useQuery({
    queryKey: promoOfferQueryKeys.list(query),
    queryFn: () => promoOfferApi.list(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreatePromoOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePromoOfferDTO) => promoOfferApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promoOfferQueryKeys.all });
      toast.success('Promo offer created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create promo offer');
    },
  });
};

export const useUpdatePromoOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePromoOfferDTO }) =>
      promoOfferApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promoOfferQueryKeys.all });
      toast.success('Promo offer updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update promo offer');
    },
  });
};

export const useUpdatePromoOfferStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      promoOfferApi.update(id, { is_active }),
    onMutate: async ({ id, is_active }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: promoOfferQueryKeys.lists() });

      // Get all queries that match the list pattern
      const queries = queryClient.getQueriesData<PromoOfferListResponse>({
        queryKey: promoOfferQueryKeys.lists(),
      });

      // Store all previous data
      const previousQueries = queries.map(([queryKey, data]) => ({
        queryKey,
        data,
      }));

      // Optimistically update all matching queries
      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<PromoOfferListResponse>(queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            promo_offers: old.promo_offers.map((promoOffer) =>
              promoOffer.id === id ? { ...promoOffer, is_active } : promoOffer
            ),
          };
        });
      });

      // Return a context with all previous data
      return { previousQueries };
    },
    onError: (_, __, context) => {
      // If the mutation fails, restore all previous queries
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          if (data) {
            queryClient.setQueryData(queryKey, data);
          }
        });
      }
      toast.error('Failed to update promo offer status');
    },
    onSuccess: () => {
      toast.success('Promo offer status updated');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: promoOfferQueryKeys.all });
    },
  });
};

export const useDeletePromoOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => promoOfferApi.delete(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: promoOfferQueryKeys.lists() });

      // Get all queries that match the list pattern
      const queries = queryClient.getQueriesData<PromoOfferListResponse>({
        queryKey: promoOfferQueryKeys.lists(),
      });

      // Store all previous data
      const previousQueries = queries.map(([queryKey, data]) => ({
        queryKey,
        data,
      }));

      // Optimistically update all matching queries
      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<PromoOfferListResponse>(queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            promo_offers: old.promo_offers.filter((promoOffer) => promoOffer.id !== id),
            total_count: old.total_count - 1,
          };
        });
      });

      // Return a context with all previous data
      return { previousQueries };
    },
    onError: (_, __, context) => {
      // If the mutation fails, restore all previous queries
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ queryKey, data }) => {
          if (data) {
            queryClient.setQueryData(queryKey, data);
          }
        });
      }
      toast.error('Failed to delete promo offer');
    },
    onSuccess: () => {
      toast.success('Promo offer deleted successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: promoOfferQueryKeys.all });
    },
  });
};